import * as React from "react";

import { cn } from "@/lib/utils";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
} from "@/components/ui/dialog";
import { FormItem, FormLabel } from "@/components/ui/form";
import { useAppContext } from "@/providers/app-context";
import { zodResolver } from "@hookform/resolvers/zod";
import AuthCode from "react-auth-code-input";
import { useForm } from "react-hook-form";
import * as z from "zod";
import FormTemplate, { FormSectionTemplate } from "../form-template";
import { ButtonLoading } from "../ui/button-loading";
import { SimpleLink } from "../ui/simple-link";
import { DialogTitle } from "@radix-ui/react-dialog";
import { useMutation } from "@tanstack/react-query";
import { requestPasswordReset } from "@/api/user";
import { toast } from "../ui/use-toast";
import { Checkbox } from "../ui/checkbox";
import { isApiError } from "@/api/_client";
import { isMFACheck } from "@/api/auth";

const formSchema = z.object({
  email: z.string().email({
    message: "Required",
  }),
  password: z.string().nonempty({
    message: "Required",
  }),
  rememberMe: z.boolean(),
});
type FormFV = z.infer<typeof formSchema>;

const passwordResetSchema = z.object({
  email: z.string().email({
    message: "Required",
  }),
});

type UserAuthFormProps = React.HTMLAttributes<HTMLDivElement>;

export function UserAuthForm({ className, ...props }: UserAuthFormProps) {
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [mfa, setMfa] = React.useState<string>();
  const [mfaStatus, setMfaStatus] = React.useState<string>();
  const [mfaError, setMfaError] = React.useState<string | undefined>(undefined);
  const [isTrusted, setIsTrusted] = React.useState(false);

  const [showPasswordResetDialog, setShowPasswordResetDialog] =
    React.useState(false);

  const [showMFADialog, setShowMFADialog] = React.useState(false);

  const { logIn } = useAppContext();

  const authFormTemplate = (
    passwordResetDialog: () => void,
  ): FormSectionTemplate<FormFV>[] => [
    {
      fields: [
        {
          name: "email",
          showTopLabel: true,
          label: "Email",
          placeholder: "Enter your account email",
          type: "text",
        },
        {
          name: "password",
          label: "Password",
          showTopLabel: true,
          type: "password",
          placeholder: "Enter your password",
        },
        // {
        //   name: "rememberMe",
        //   label: "Remember me",
        //   type: "checkbox",
        //   className: "col-span-6",
        // },
        {
          className: "col-span-12 mt-0",
          customComponent: (
            <>
              <FormItem className="flex flex-1 flex-row space-x-2 space-y-0">
                <FormLabel className="w-full flex-1 text-right text-cf-foreground">
                  <SimpleLink onClick={passwordResetDialog}>
                    Forgot your password?
                  </SimpleLink>
                </FormLabel>
              </FormItem>
            </>
          ),
        },
      ],
    },
  ];

  const passwordResetFormTemplate: FormSectionTemplate<FormFV>[] = [
    {
      fields: [
        {
          name: "email",
          showTopLabel: false,
          label: "Email",
          placeholder: "Enter your account email",
          type: "text",
        },
      ],
    },
  ];

  const defaultValues = {
    email: "",
    password: "",
    rememberMe: false,
  };

  const form = useForm<FormFV>({
    resolver: zodResolver(formSchema),
    defaultValues: defaultValues,
    mode: "onSubmit",
  });

  type PassFV = z.infer<typeof passwordResetSchema>;
  const passwordResetForm = useForm<PassFV>({
    resolver: zodResolver(passwordResetSchema),
    defaultValues: defaultValues,
    mode: "onSubmit",
  });

  async function onSubmit(data: FormFV) {
    setMfaError(undefined);
    setMfaStatus(undefined);
    setIsLoading(true);
    const { email, password } = data;
    try {
      const res = await logIn({ email, password, mfa, isTrusted });
      if (isApiError(res)) {
        setMfaError(res.error);
      } else if (isMFACheck(res)) {
        if (res.data.mfa) {
          setShowMFADialog(true);
        }
        if (res.data.mfa_status) {
          setMfaStatus(res.data.mfa_status);
        }
      }
    } catch (error) {
      if (typeof error === "string")
        form.setError("root", { type: "manual", message: error });
      else throw error;
    }

    setIsLoading(false);
  }

  const requestPasswordResetMutation = useMutation({
    mutationFn: async ({ email }: { email: string }) => {
      const res = await requestPasswordReset(email);
      if (isApiError(res)) {
        toast({
          title: "An error occurred",
          description: `Please try again later.`,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Password reset requested",
          description: `If you are registered with us, you will receive an email with instructions on how to reset your password.`,
        });
        setShowPasswordResetDialog(false);
      }
    },
  });

  return (
    <div className={cn("grid gap-6", className)} {...props}>
      <FormTemplate<FormFV>
        form={form}
        sections={authFormTemplate(() => setShowPasswordResetDialog(true))}
        onSubmit={onSubmit}
        isLoading={isLoading}
        submitLabel="Log in"
      />
      <Dialog open={showMFADialog}>
        <DialogContent showCloseButton={false}>
          <DialogTitle className="sr-only">
            Multi-factor authentication
          </DialogTitle>
          <DialogHeader>
            <DialogDescription className="flex w-full flex-col gap-y-2 text-center">
              {mfaStatus === "too_many_attempts" ? (
                <div className="text-base font-semibold text-red-600">
                  <p className="mb-4">
                    You failed to enter the correct code too many times.
                  </p>
                  <p>Please try again later.</p>
                </div>
              ) : (
                <>
                  <div className="text-lg font-semibold">Check your inbox</div>
                  <div className="mb-6 text-base">
                    We sent a 6-digit code to your email. Please enter it below
                    to log in.
                  </div>
                  <div
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && mfa?.length === 6) {
                        onSubmit(form.getValues());
                      }
                    }}
                  >
                    <AuthCode
                      allowedCharacters="numeric"
                      onChange={setMfa}
                      containerClassName="flex flex-row justify-center gap-x-2 mb-2"
                      inputClassName="border w-12 h-12 rounded-md text-center text-2xl focus:outline-none focus:ring-2 focus:ring-green-600 focus:border-transparent"
                    />
                  </div>
                  {mfaStatus === "code_invalid" && (
                    <p className="text-red-600">
                      The code you entered is not valid. Please try again.
                    </p>
                  )}
                  {mfaStatus === "code_expired" && (
                    <p className="text-red-600">
                      Your code has expired. Please request a new one.
                    </p>
                  )}
                  <div
                    className="my-2 flex w-full items-center space-x-4 rounded-lg bg-slate-50 px-4 py-2 text-left hover:cursor-pointer hover:bg-slate-100"
                    onClick={() => {
                      setIsTrusted((prev) => !prev);
                    }}
                  >
                    <Checkbox checked={isTrusted} />
                    <div className="grid gap-1.5 leading-none">
                      <div className="text-sm font-medium leading-none">
                        This is a trusted device
                      </div>
                      <p className="text-sm text-muted-foreground">
                        We will not ask for verification code on your trusted
                        device for 7 days when you log in.
                      </p>
                    </div>
                  </div>
                  {mfaError && <p className="text-red-600">{mfaError}</p>}
                  <ButtonLoading
                    className="mt-4"
                    size="lg"
                    isLoading={isLoading}
                    onClick={() => onSubmit(form.getValues())}
                  >
                    Submit
                  </ButtonLoading>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
      <Dialog
        open={showPasswordResetDialog}
        onOpenChange={setShowPasswordResetDialog}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Password reset</DialogTitle>
            <DialogDescription className="pt-4">
              <FormTemplate<PassFV>
                form={passwordResetForm}
                sections={passwordResetFormTemplate}
                onSubmit={requestPasswordResetMutation.mutateAsync}
                isLoading={requestPasswordResetMutation.isPending}
                submitLabel="Request password reset"
              />
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </div>
  );
}
